import Grid from '@material-ui/core/Grid';
import { Theme, withStyles, WithStyles, withTheme } from '@material-ui/core/styles';
import { IActivity } from '@pbl/pbl-react-core/lib/models/activity/types';
import { ISurveyQuestionResponse } from '@pbl/pbl-react-core/lib/models/survey/v2/types';
import AppSpinner from '@pbl/pbl-react-web-components/lib/app-spinner/AppSpinner';
import EarnActivityHeader from '@pbl/pbl-react-web-components/lib/earn/earn-activity-header';
import { EarnPointsModal } from '@pbl/pbl-react-web-components/lib/package';
import SurveyContent from '@pbl/pbl-react-web-components/lib/survey/v2/SurveyContent';
import styles from 'assets/jss/modules/survey/SurveyScreenStyle';
import constants from 'config/constants';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Prompt, RouteComponentProps } from 'react-router-dom';
import { IRootState } from 'redux/reducers';
import { getActivity, getActivityWithFreqCap, resetActivityCompleted, setActivityCompleted } from 'redux/reducers/activity/actions';
import { showMessageBar } from 'redux/reducers/app/actions';
import { setIsFormDirty } from 'redux/reducers/forms-metadata/actions';
import { fetchUserPoints } from 'redux/reducers/ledger/actions';
import { getProfile } from 'redux/reducers/profile/actions';
import { getSurvey, submit } from 'redux/reducers/survey/action';
import { fetchSurvey, fetchUserSurveyResponse, postSurvey, reset, updateSurveyPostResponse } from 'redux/reducers/survey/v2/action';
import { scrollToTheTop } from 'utils/htmlUtil';

interface IMatchParams {
  activityId: string;
}
interface ISurveyScreenProps
  extends StateProps,
    DispatchProps,
    RouteComponentProps<IMatchParams>,
    WithStyles<typeof styles>,
    WithTranslation {
  theme: Theme;
}

interface ISurveyScreenState {
  displayImage: boolean;
  loading: boolean;
  isAnswered: boolean;
  surveyUpdated: boolean;
  selectedActivity?: IActivity;
}

type Props = StateProps & DispatchProps & ISurveyScreenProps;

class SurveyScreen extends React.Component<Props, ISurveyScreenState> {
  constructor(props: Props) {
    super(props);
    this.state = { displayImage: false, loading: true, isAnswered: false, surveyUpdated: false, selectedActivity: undefined };
  }

  public async componentDidMount() {
    document.title = 'Survey Activity';
    scrollToTheTop();
    const {
      match: { params }
    } = this.props;
    if (params.activityId.trim().length > 0) {
      await this.getData();
    } else {
      await this.showInvalidActivityError();
    }
  }

  public componentWillUnmount() {
    this.props.reset();
    this.props.setIsFormDirty(false);
  }

  public async getData() {
    const {
      match: {
        params: { activityId }
      }
    } = this.props;
    const activityData = this.props.isLoggedIn
      ? await this.props.getActivity(Number(activityId))
      : await this.props.getActivityWithFreqCap(Number(activityId));

    if (activityData) {
      this.setState({ selectedActivity: activityData });
      this.props.fetchSurvey(activityData.key);

      // For a authenticated user fetch existing response for the survey
      if (this.state.selectedActivity && this.props.isLoggedIn) {
        const surveyData = await getSurvey(activityData.key);
        await this.props.fetchUserSurveyResponse(Number(surveyData?.id));
        if (this.props.survey.surveyResults) {
          this.setState({ isAnswered: true });
        }
      }

      if (constants.INTERESTS_SURVEY.ACTIVITY_KEY === activityData.key && this.props.isLoggedIn) {
        await this.setupInterests();
      }
    } else if (this.props.isLoggedIn) {
      await this.showInvalidActivityError();
    }
    this.setState({ loading: false });
  }

  private showInvalidActivityError = async () => {
    const { history } = this.props;
    await history.push('/earn', {
      message: {
        message: `Something went wrong. Try again!`,
        type: 'error',
        messageTimeout: 10000
      }
    });
  };

  public onChange = (answer: ISurveyQuestionResponse) => {
    this.props.updateSurveyPostResponse(answer);
    this.props.setIsFormDirty(true);
  };

  public async setupInterests() {
    // check if survey is answered so we do not show prize modal a 2nd time
    await this.props.getProfile();
    const {
      survey: { surveyPost }
    } = this.props;
    if (this.props.isLoggedIn) {
      const isAnswered = surveyPost?.survey_response?.length !== 0;
      this.setState({ isAnswered: !!isAnswered });
    } else {
      this.setState({ isAnswered: false });
    }
  }

  public postSurvey = async () => {
    const { isLoggedIn } = this.props;
    const { isAnswered } = this.state;
    if (!isLoggedIn) {
      this.props.history.push('/login', { from: this.props.location });
      return;
    }
    await this.props.postSurvey();
    await this.props.fetchUserPoints();
    const {
      selectedActivity,
      survey: { surveyPostResult, errorMessage }
    } = this.props;
    if (surveyPostResult && selectedActivity && !isAnswered) {
      this.props.setActivityCompleted({
        pointsEarned: selectedActivity.amount,
        activity: selectedActivity
      });
      this.props.setIsFormDirty(false);
      if (selectedActivity.key === constants.INTERESTS_SURVEY.ACTIVITY_KEY) {
        this.props.showMessageBar({ message: 'interests.updated' });
      } else {
        this.props.showMessageBar({ message: 'survey.submitted' });
      }
    } else if (surveyPostResult && selectedActivity && isAnswered) {
      this.props.setIsFormDirty(false);
      setTimeout(async () => {
        if (selectedActivity.key === constants.INTERESTS_SURVEY.ACTIVITY_KEY) {
          this.props.showMessageBar({ message: 'interests.updated' });
        } else {
          this.props.showMessageBar({ message: 'survey.updated' });
        }
      }, 250);
      this.navigateTo();
    } else if (surveyPostResult && selectedActivity) {
      this.setState({
        surveyUpdated: true
      });
    } else if (!errorMessage) {
      this.props.history.push('/earn', {
        message: { message: `This activity is not available.`, type: 'warning', messageTimeout: 3000 }
      });
    }
  };

  public getContent() {
    const {
      survey: { survey, surveyPost },
      selectedActivity
    } = this.props;

    const bonusPoints = selectedActivity?.bonusAmount ?? 0;
    return survey ? (
      <Grid item={true} xs={12}>
        {survey ? (
          <SurveyContent
            onSave={this.postSurvey}
            onChange={this.onChange}
            survey={survey}
            surveyPostResponse={surveyPost ?? null}
            isLoading={this.props.survey.loading || this.props.loading}
            isSystemSurvey={Boolean(selectedActivity?.key === constants.INTERESTS_SURVEY.ACTIVITY_KEY)}
            bonusPoints={bonusPoints}
            questionLayout={selectedActivity?.extraData?.['image_survey_layout'] ?? null}
          />
        ) : null}
      </Grid>
    ) : null;
  }

  readonly onDismissEarnPointsModal = (): void => {
    this.props.resetActivityCompleted();
    this.props.history.push('/earn');
  };

  readonly navigateTo = (): void => {
    this.props.history.push('/earn');
  };

  public render() {
    const {
      survey: { survey, surveyResults, errorMessage },
      classes,
      activityCompleted,
      selectedActivity
    } = this.props;
    const { isAnswered } = this.state;
    const isLoadingData = this.state.loading;

    return (
      <div>
        {this.props.isLoggedIn && !errorMessage && (
          <Prompt when={this.props.formMetadata.isFormDirty} message={this.props.t('interests.promptMessage')} />
        )}
        {!surveyResults && activityCompleted ? (
          <EarnPointsModal
            pointEarned={
              // activityCompleted is set through the redux actions
              selectedActivity?.bonusAmount ?? activityCompleted?.pointsEarned ?? 0
            }
            isVisible={Boolean(activityCompleted)}
            onClose={this.onDismissEarnPointsModal}
          />
        ) : null}
        {survey && (
          <div className={classes.container}>
            {isLoadingData && this.props.survey.loading ? (
              <AppSpinner label={'Loading Activities'} />
            ) : (
              <Grid className={classes.container} container={true} spacing={2}>
                <Grid item={true} xs={12}>
                  <EarnActivityHeader
                    data={{
                      image: survey.image_url.replace('100/100', 'w/h'),
                      title: selectedActivity?.title ?? survey?.title,
                      description: selectedActivity?.description ?? '',
                      points: survey.num_points,
                      preview_url: selectedActivity?.videoUrl ?? undefined,
                      isCoverImage: true
                    }}
                    bonusPoints={selectedActivity?.bonusAmount ?? 0}
                    limitReached={isAnswered}
                    navigateTo={this.navigateTo}
                  />
                  {this.getContent()}
                </Grid>
              </Grid>
            )}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = ({
  surveyV2,
  authentication: { accessToken, account },
  activityState: { selectedActivity, loading, activityCompleted },
  formMetadata
}: IRootState) => ({
  isLoggedIn: !!accessToken && accessToken.length > 0 && !!account && !!account.email,
  survey: surveyV2,
  selectedActivity,
  loading,
  activityCompleted,
  formMetadata
});

const mapDispatchToProps = {
  fetchSurvey,
  updateSurveyPostResponse,
  postSurvey,
  submit,
  fetchUserPoints,
  getActivity,
  getActivityWithFreqCap,
  setActivityCompleted,
  resetActivityCompleted,
  reset,
  getProfile,
  fetchUserSurveyResponse,
  showMessageBar,
  setIsFormDirty
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(
  mapStateToProps,
  mapDispatchToProps
  // @ts-ignore
)(withTheme(withTranslation()(withStyles(styles)(SurveyScreen))));
